import { useEffect, useState } from "react";
import { FaEllipsisV, FaTrash } from "react-icons/fa";
import RandomColorCircle from "../utils/RandomColorCircle";
import { ASSIGNABLE_ROLES } from "./ShareModal";

/**
 * ExpensesSummary - "Collaborators" card: who's on the scheda and how much
 * each has spent, top spender called out. Styled after the Stitch
 * "Dettaglio Scheda Mobile" / "Dettaglio Scheda Spese: Dining Q4" reference
 * screens. Total (previously duplicated here) now lives in the scheda-hero
 * card instead.
 *
 * On the detail page (variant="default"), a collaborator with a matching
 * `scheda.utentiList` entry gets a kebab menu with the same role-change /
 * remove operations as ShareModal, gated the same way: only if the current
 * user canAggiungereUtente, the target isn't the Owner, and isn't yourself.
 */
const ExpensesSummary = ({
  scheda,
  expencersWithTotals,
  maxExpencer,
  showCompare = true,
  variant = "default", // "default" | "compact" — also gates the management menu
  currentUserEmail,
  onChangeRole,
  onRemoveUser,
}) => {
  const [openMenuKey, setOpenMenuKey] = useState(null);

  useEffect(() => {
    if (!openMenuKey) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest("[data-collab-menu]")) {
        setOpenMenuKey(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuKey]);

  if (!scheda.notaSpese || scheda.notaSpese.length === 0) {
    return null;
  }

  const hasMultipleUsers = expencersWithTotals && expencersWithTotals.length > 1;
  if (!showCompare || !hasMultipleUsers) {
    return null;
  }

  const canManage =
    variant === "default" && !!onChangeRole && !!onRemoveUser && !!scheda.myPermissions?.canAggiungereUtente;
  const utentiList = scheda.utentiList || [];

  const topSpenderName = maxExpencer?.userName ?? maxExpencer;
  const sortedExpencers = [...expencersWithTotals].sort((a, b) => b.totalExp - a.totalExp);
  const heading = variant === "compact" ? "Compare" : "Collaborators";

  return (
    <div className="dashboard-card p-3">
      <h6 className="mb-2">{heading}</h6>
      <div className="expenses-collab-list">
        {sortedExpencers.map((item) => {
          const utenteEntry = utentiList.find(
            (u) => u.userId && String(u.userId) === String(item.userId)
          );
          const canManageThisUser =
            canManage &&
            utenteEntry &&
            utenteEntry.role !== "Owner" &&
            utenteEntry.email !== currentUserEmail;

          return (
            <div className="expenses-collab-row" key={item.userId || item.userName}>
              <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                <RandomColorCircle
                  letter={item.userName}
                  tooltip={item.userName}
                  email={item.userId || item.userName}
                  className="circle-small"
                />
                <p className="expenses-collab-name">{item.userName}</p>
              </div>
              <div className="d-flex align-items-center gap-1">
                <p className="expenses-collab-amount">€ {item.totalExp.toFixed(2)}</p>
                {item.userName === topSpenderName && (
                  <span className="expenses-collab-badge">Top spender</span>
                )}
                {canManageThisUser && (
                  <div className="scheda-row-actions" data-collab-menu>
                    <button
                      type="button"
                      className="scheda-row-actions-toggle"
                      aria-label={`Manage ${utenteEntry.email}`}
                      onClick={() =>
                        setOpenMenuKey((key) => (key === utenteEntry._id ? null : utenteEntry._id))
                      }
                    >
                      <FaEllipsisV size={12} />
                    </button>
                    {openMenuKey === utenteEntry._id && (
                      <div className="scheda-row-actions-menu">
                        {ASSIGNABLE_ROLES.filter((role) => role !== utenteEntry.role).map((role) => (
                          <button
                            key={role}
                            type="button"
                            onClick={() => {
                              onChangeRole(utenteEntry, role);
                              setOpenMenuKey(null);
                            }}
                          >
                            Make {role}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="text-danger"
                          onClick={() => {
                            onRemoveUser(utenteEntry);
                            setOpenMenuKey(null);
                          }}
                        >
                          <FaTrash size={12} /> Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ExpensesSummary;
